// Pure column-model helpers for DataTable: which columns are shown, in what order, how that
// choice is persisted, and how filter fields are derived from the same column declarations.
// No React and no extension-less imports, so node:test can load this file directly.
//
// A column declaration (all optional except key + header):
//   { key, header, render?, sortKey?, align?, className?, headerClassName?,
//     defaultVisible?: false   hidden until the user adds it from the Columns menu
//     locked?: true            cannot be hidden or moved (primary name column, row actions)
//     label?: string           menu / chip name when `header` is not a string
//     filter?: { type, key?, label?, apiKey?, apiRange?, options?, ... } }  see filters/registry.js

export const STORAGE_PREFIX = 'um_ui_cols:';

const isLocked = (col) => col.locked === true;
const startsVisible = (col) => isLocked(col) || col.defaultVisible !== false;

/** Menu / chip name of a column ('' when it has none, e.g. the icon-only actions column). */
export const columnLabel = (col) => col.label || (typeof col.header === 'string' ? col.header : '');

/** Columns the Columns menu lists: those with a name. */
export const chooserColumns = (columns) => columns.filter((c) => columnLabel(c));

/** The declared default: every column in declared order; visible unless `defaultVisible: false`. */
export const defaultConfig = (columns) => ({
    order: columns.map((c) => c.key),
    visible: columns.filter(startsVisible).map((c) => c.key),
});

/**
 * Reconciles a saved config with the current declarations: unknown keys are dropped, columns
 * added since are appended (visible per their default), locked columns are always visible.
 */
export const sanitizeConfig = (saved, columns) => {
    const base = defaultConfig(columns);
    if (!saved || !Array.isArray(saved.order) || !Array.isArray(saved.visible)) return base;
    const known = new Set(base.order);
    const order = [...new Set(saved.order.filter((k) => known.has(k)))];
    const wasSaved = new Set(order);
    const savedVisible = new Set(saved.visible);
    const visible = new Set();
    columns.forEach((c) => {
        const isNew = !wasSaved.has(c.key);
        if (isNew) order.push(c.key);
        if (isLocked(c) || (isNew ? startsVisible(c) : savedVisible.has(c.key))) visible.add(c.key);
    });
    return { order, visible: order.filter((k) => visible.has(k)) };
};

export const isDefaultConfig = (config, columns) => {
    const base = defaultConfig(columns);
    const same = (a, b) => a.length === b.length && a.every((k, i) => k === b[i]);
    return same(config.order, base.order) && same(config.visible, base.visible);
};

/** Shows or hides one column. Locked columns cannot be hidden. */
export const toggleColumn = (config, columns, key) => {
    const col = columns.find((c) => c.key === key);
    if (!col || isLocked(col)) return config;
    const next = new Set(config.visible);
    if (!next.delete(key)) next.add(key);
    return { ...config, visible: config.order.filter((k) => next.has(k)) };
};

/** Moves an unlocked column one place among the unlocked columns (delta -1 earlier, +1 later). */
export const moveColumn = (config, columns, key, delta) => {
    const locked = new Set(columns.filter(isLocked).map((c) => c.key));
    if (locked.has(key)) return config;
    const movable = config.order.filter((k) => !locked.has(k));
    const from = movable.indexOf(key);
    const to = from + delta;
    if (from === -1 || to < 0 || to >= movable.length) return config;
    movable.splice(to, 0, movable.splice(from, 1)[0]);
    let i = 0;
    return { ...config, order: config.order.map((k) => (locked.has(k) ? k : movable[i++])) };
};

/** Keys of columns the table must show because an applied filter is on them. */
export const revealedColumnKeys = (fields, isActive) =>
    fields.filter((f) => f.columnKey && isActive(f)).map((f) => f.columnKey);

/**
 * The columns to render: visible ones (plus `forced`, e.g. columns an applied filter is on),
 * in the saved order. Locked columns keep their declared slot; the rest fill the other slots
 * in saved order.
 */
export const resolveColumns = (columns, config, forced = []) => {
    const shown = new Set([...config.visible, ...forced]);
    const rank = new Map(config.order.map((k, i) => [k, i]));
    const movable = columns
        .filter((c) => !isLocked(c))
        .sort((a, b) => (rank.get(a.key) ?? 1e9) - (rank.get(b.key) ?? 1e9));
    let i = 0;
    return columns.map((c) => (isLocked(c) ? c : movable[i++])).filter((c) => isLocked(c) || shown.has(c.key));
};

/** Filter fields from the columns' `filter` declarations, named after the column header. */
export const filterFieldsFromColumns = (columns) =>
    columns
        .filter((c) => c.filter)
        .map((c) => ({
            ...c.filter,
            key: c.filter.key || c.key,
            label: c.filter.label || columnLabel(c) || c.key,
            columnKey: c.key,
        }));

// --- persistence (localStorage; every access guarded, the table works without it) ----------

const defaultStorage = () => {
    try {
        return typeof window !== 'undefined' ? window.localStorage : null;
    } catch {
        return null;
    }
};

export const loadConfig = (tableKey, columns, storage = defaultStorage()) => {
    try {
        const raw = storage && tableKey ? storage.getItem(STORAGE_PREFIX + tableKey) : null;
        return sanitizeConfig(raw ? JSON.parse(raw) : null, columns);
    } catch {
        return defaultConfig(columns);
    }
};

/** Stores the config, or removes the entry when it equals the default. */
export const saveConfig = (tableKey, config, columns, storage = defaultStorage()) => {
    try {
        if (!storage || !tableKey) return;
        if (isDefaultConfig(config, columns)) storage.removeItem(STORAGE_PREFIX + tableKey);
        else storage.setItem(STORAGE_PREFIX + tableKey, JSON.stringify(config));
    } catch {
        // Storage unavailable or full: the choice then lasts for this visit only.
    }
};
